import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaSyncAlt, FaBolt, FaHandHolding, FaDatabase, FaCogs, FaCheck } from 'react-icons/fa';


// Interface para tipos de card
interface IntegrationCardProps {
  type: 'protheus' | 'corporate';
}

const EasyMovePage = () => {
  // Efeito para animação fade-in no scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => {
      document.querySelectorAll('.animate-on-scroll').forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        {/* Decorações abstratas */}
        <HeroDecorations>
          <BlurCircle position="top-left" size="24rem" color="rgba(96, 165, 250, 0.2)" blur="50px" />
          <BlurCircle position="bottom-right" size="32rem" color="rgba(103, 232, 249, 0.1)" blur="60px" />
          <BlurCircle position="top-right" size="16rem" color="rgba(59, 130, 246, 0.2)" blur="40px" />
        </HeroDecorations>
        
        <HeroContainer>
          <HeroGrid>
            <HeroContent className="animate-on-scroll">
              <HeroBadge>Solução Premium</HeroBadge>
              <HeroTitle>
                Easy<GradientText>Move</GradientText>
              </HeroTitle>
              <HeroSubtitle>Revolucione sua Gestão de Estoque</HeroSubtitle>
              <HeroDescription>
                O EasyMove é uma solução inovadora que integra de forma nativa com o TOTVS Protheus, 
                oferecendo uma gestão de estoque ágil, inteligente e segura.
                <br /><br />
                Com operações até 60% mais rápidas e sincronização em tempo real, o sistema permite 
                um controle minucioso e automatizado, eliminando erros manuais e proporcionando 
                uma experiência otimizada.
              </HeroDescription>
              <HeroCTA>
                <PrimaryButton as="a" href="https://wa.me/553498055971" target="_blank" rel="noopener noreferrer">
                  Falar com nosso time
                </PrimaryButton>
                <SecondaryButton>
                  Demonstração 
                  <ButtonArrow>→</ButtonArrow>
                </SecondaryButton>
              </HeroCTA>
            </HeroContent>
            <HeroImageContainer className="animate-on-scroll">
              <GlowEffect />
              <HeroImage>
                <FaCogs size={150} color="#4d9fff" />
              </HeroImage>
            </HeroImageContainer>
          </HeroGrid>
        </HeroContainer>
      </HeroSection>

      {/* Features Section */}
      <SectionContainer>
        <SectionTitle className="animate-on-scroll">
          <SectionTitleText>Recursos Exclusivos</SectionTitleText>
          <TitleUnderline />
        </SectionTitle>
        
        <FeaturesGrid>
          {/* Feature Card 1 */}
          <FeatureCard className="animate-on-scroll">
            <FeatureIconContainer>
              <FaSyncAlt size={32} color="#2176ff" />
            </FeatureIconContainer>
            <FeatureTitle>Integração Total</FeatureTitle>
            <FeatureText>
              Conexão perfeita com TOTVS Protheus e outros ERPs, garantindo que todas as 
              operações estejam sincronizadas em tempo real para uma gestão sem falhas.
            </FeatureText>
          </FeatureCard>

          {/* Feature Card 2 */}
          <FeatureCard className="animate-on-scroll">
            <FeatureIconContainer>
              <FaBolt size={32} color="#2176ff" />
            </FeatureIconContainer>
            <FeatureTitle>Alta Performance</FeatureTitle>
            <FeatureText>
              Processamentos 60% mais rápidos que as soluções tradicionais, otimizando 
              as operações e aumentando a produtividade da sua empresa.
            </FeatureText>
          </FeatureCard>

          {/* Feature Card 3 */}
          <FeatureCard className="animate-on-scroll">
            <FeatureIconContainer>
              <FaHandHolding size={32} color="#2176ff" />
            </FeatureIconContainer>
            <FeatureTitle>Processo Eficiente</FeatureTitle>
            <FeatureText>
              Otimização de fluxos operacionais, reduzindo o tempo de execução e aumentando 
              a produtividade em todas as etapas do processo.
            </FeatureText>
          </FeatureCard>
        </FeaturesGrid>
      </SectionContainer>

      {/* Detalhes do Produto */}
      <FeaturedProductSection>
        <SectionContainer>
          <SectionTitle className="animate-on-scroll">
        <SectionTitleText>Por que o EasyMove é a escolha certa?</SectionTitleText>
        <TitleUnderline />
          </SectionTitle>

          <FeaturedProductWrapper>
        <FeaturedProductCard className="animate-on-scroll">
          <ColorBorder />
          <NewBadge>Novidade</NewBadge>

          <ProductIcon>EM</ProductIcon>

          <ProductName>EasyMove</ProductName>
          <ProductTagline>Gestão de Estoque Redefinida</ProductTagline>
          
          <ProductDescription>
            Descubra uma nova era na gestão de estoques com o EasyMove. Totalmente integrado ao TOTVS Protheus, ele oferece sincronização em tempo real e desempenho superior, garantindo eficiência e precisão.
          </ProductDescription>
          
          <Divider />
          
          <FeatureList>
            {[
          "Integração Completa com TOTVS Protheus",
          "Sincronização Automática e Instantânea",
          "Interface Intuitiva e Adaptável",
          "Segurança Avançada e Confiabilidade",
          "Suporte Técnico Especializado",
            ].map((feature, index) => (
          <FeatureItem key={index}>
            <FeatureCheck>✓</FeatureCheck>
            <FeatureText>{feature}</FeatureText>
          </FeatureItem>
            ))}
          </FeatureList>
          <ActionButton as="a" href="https://wa.me/553498055971" target="_blank" rel="noopener noreferrer">
            Agendar Demonstração
            <ButtonArrow>→</ButtonArrow>
          </ActionButton>
        </FeaturedProductCard>
        
        <FeaturedProductContent className="animate-on-scroll">
          <FeaturedProductTitle>EasyMove</FeaturedProductTitle>
          <FeaturedProductSubtitle>Inovação na Gestão de Estoques</FeaturedProductSubtitle>
          <FeaturedProductDescription>
            O EasyMove é a solução definitiva para empresas que buscam eficiência e controle. Com integração nativa ao TOTVS Protheus, ele oferece uma experiência personalizada e resultados comprovados.
          </FeaturedProductDescription>
          <BenefitsList>
            <BenefitItem>
          <BenefitIcon>⚡</BenefitIcon>
          <BenefitText>Operações até 60% mais rápidas</BenefitText>
            </BenefitItem>
            <BenefitItem>
          <BenefitIcon>📈</BenefitIcon>
          <BenefitText>Precisão de 99.8% no gerenciamento de dados</BenefitText>
            </BenefitItem>
            <BenefitItem>
          <BenefitIcon>💡</BenefitIcon>
          <BenefitText>Redução de até 30% nos custos operacionais</BenefitText>
            </BenefitItem>
          </BenefitsList>
        </FeaturedProductContent>
          </FeaturedProductWrapper>
        </SectionContainer>
      </FeaturedProductSection>

      {/* Integration Section */}
      <IntegrationSection id="integracao">
        <HeroDecorations>
          <BlurCircle position="left-center" size="24rem" color="rgba(96, 165, 250, 0.05)" blur="50px" />
          <BlurCircle position="right-bottom" size="18rem" color="rgba(103, 232, 249, 0.05)" blur="40px" />
        </HeroDecorations>
        
        <SectionContainer>
          <SectionTitle className="animate-on-scroll">
            <SectionTitleText style={{ color: 'white' }}>Integração Inteligente</SectionTitleText>
            <TitleUnderline />
          </SectionTitle>
          
          <IntegrationGrid>
            {/* Integration Card 1 */}
            <IntegrationCard type="protheus" className="animate-on-scroll">
              <IntegrationContent>
                <IntegrationIconContainer>
                  <FaDatabase size={40} color="#4d9fff" />
                </IntegrationIconContainer>
                <IntegrationTitle>TOTVS Protheus</IntegrationTitle>
                <IntegrationText>
                  Sincronização bidirecional em tempo real com o ERP líder do mercado, 
                  proporcionando uma gestão completa e integrada dos estoques.
                </IntegrationText>
                <IntegrationText>
                  Essa integração elimina a necessidade de lançamentos manuais, reduzindo 
                  erros e aumentando a eficiência operacional.
                </IntegrationText>
              </IntegrationContent>
            </IntegrationCard>

            {/* Integration Card 2 */}
            <IntegrationCard type="corporate" className="animate-on-scroll">
              <IntegrationContent>
                <IntegrationIconContainer>
                  <FaCogs size={40} color="#ffffff" />
                </IntegrationIconContainer>
                <IntegrationTitle>Infraestrutura Corporativa</IntegrationTitle>
                <IntegrationText>
                  Projetado para atender às demandas estruturais da sua empresa, garantindo 
                  integração eficiente com sistemas internos e externos.
                </IntegrationText>
                <IntegrationText>
                  Nossa solução é robusta, segura e escalável, suportando operações críticas 
                  e impulsionando a transformação digital do seu negócio.
                </IntegrationText>
              </IntegrationContent>
            </IntegrationCard>
          </IntegrationGrid>
        </SectionContainer>
      </IntegrationSection>

      {/* Stats Section */}
      <StatsSection id="estatisticas">
        <SectionContainer>
          <SectionTitle className="animate-on-scroll">
            <SectionTitleText>Resultados Comprovados</SectionTitleText>
            <TitleUnderline />
          </SectionTitle>
          
          <StatsGrid>
            {/* Stat Card 1 */}
            <StatCard className="animate-on-scroll">
              <StatNumber>60%</StatNumber>
              <StatText>Mais Eficiente nas operações</StatText>
            </StatCard>
            
            {/* Stat Card 2 */}
            <StatCard className="animate-on-scroll">
              <StatNumber>24/7</StatNumber>
              <StatText>Disponibilidade garantida</StatText>
            </StatCard>
            
            {/* Stat Card 3 */}
            <StatCard className="animate-on-scroll">
              <StatNumber>100%</StatNumber>
              <StatText>Dados sempre sincronizados</StatText>
            </StatCard>
          </StatsGrid>
        </SectionContainer>
      </StatsSection>
    </PageContainer>
  );
};

// Estilos CSS
const GlobalStyle = styled.div`
  .animate-on-scroll {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }

  .animate-on-scroll.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Estilos Base
const PageContainer = styled.div`
  background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  padding: 5rem 2rem 6rem;
  background: radial-gradient(circle, rgba(4,16,22,1) 0%, rgba(4,44,64,1) 100%);
  color: white;
  overflow: hidden;
  
  @media (min-width: 1024px) {
    padding: 6rem 2rem 8rem;
  }
`;

const HeroDecorations = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.2;
`;

interface BlurCircleProps {
  position: string;
  size: string;
  color: string;
  blur: string;
}

const BlurCircle = styled.div<BlurCircleProps>`
  position: absolute;
  border-radius: 50%;
  background: ${props => props.color};
  width: ${props => props.size};
  height: ${props => props.size};
  filter: blur(${props => props.blur});
  
  ${props => {
    switch(props.position) {
      case 'top-left':
        return `top: 5%; left: 10%;`;
      case 'top-right':
        return `top: 10%; right: 15%;`;
      case 'bottom-right':
        return `bottom: 5%; right: 20%;`;
      case 'left-center':
        return `top: 40%; left: 0;`;
      case 'right-bottom':
        return `bottom: 10%; right: 10%;`;
      default:
        return '';
    }
  }}
`;

const HeroContainer = styled.div`
  padding-top: 2rem;
  max-width: 1280px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const HeroContent = styled.div`
  max-width: 600px;
`;

const HeroBadge = styled.span`
  background: linear-gradient(to right,rgb(24, 118, 159), #2563eb);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.85rem;
  border-radius: 1rem;
  display: inline-block;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 5px rgba(56, 189, 248, 0.3);
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 1rem;
  
  @media (min-width: 640px) {
    font-size: 3.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(to right, #bfdbfe, #7dd3fc);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const GradientTextLight = styled.span`
  background: linear-gradient(to right, #f0f9ff, #e0f2fe);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 1.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

const HeroDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
`;

const HeroCTA = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(to right, #38bdf8, #2563eb);
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 4px 6px rgba(56, 189, 248, 0.2);
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(56, 189, 248, 0.3);
  }
`;

const SecondaryButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: transform 0.3s, background 0.3s;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  
  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ButtonArrow = styled.span`
  margin-left: 0.5rem;
  transition: transform 0.2s;
  
  ${SecondaryButton}:hover & {
    transform: translateX(4px);
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 350px;

  @media (max-width: 640px) {
    display: none;
  }
`;

const GlowEffect = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.3) 0%, rgba(0, 0, 0, 0) 70%);
  border-radius: 50%;
`;

const HeroImage = styled.div`
  position: relative;
  z-index: 1;
  animation: float 6s ease-in-out infinite;

  @keyframes float {
    0% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-15px);
    }
    100% {
      transform: translateY(0px);
    }
  }
`;

// Section Container
const SectionContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 5rem 2rem;
  
  @media (min-width: 1024px) {
    padding: 6rem 2rem;
  }
`;

// Section Title
const SectionTitle = styled.h2`
  position: relative;
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitleText = styled.span`
  color: #003554;
  font-size: 2rem;
  font-weight: 700;
  
  @media (min-width: 640px) {
    font-size: 2.5rem;
  }
`;

const TitleUnderline = styled.span`
  position: absolute;
  height: 4px;
  width: 6rem;
  background: linear-gradient(to right, #38bdf8, #2563eb);
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
`;

// Features Section
const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }
`;

const FeatureIconContainer = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  background: rgba(56, 189, 248, 0.1);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const FeatureTitle = styled.h3`
  color: #003554;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1rem;
`;

const FeatureText = styled.p`
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
`;

// Featured Product Section
const FeaturedProductSection = styled.section`
  background: linear-gradient(to right, #f1f5f9, #e2e8f0);
  padding: 1rem 0;
`;

const FeaturedProductWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    align-items: center;
  }
`;

const FeaturedProductCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeaturedProductContent = styled.div`
  padding: 1rem;
`;

const FeaturedProductTitle = styled.h3`
  color: #003554;
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
`;

const FeaturedProductSubtitle = styled.p`
  color: #38bdf8;
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 1.5rem;
`;

const FeaturedProductDescription = styled.p`
  color: #475569;
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
`;

const BenefitIcon = styled.span`
  font-size: 1.25rem;
  margin-right: 1rem;
`;

const BenefitText = styled.span`
  color: #334155;
  font-size: 1.1rem;
`;

const ColorBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, #38bdf8, #2563eb);
`;

const NewBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(to right, #38bdf8, #2563eb);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  box-shadow: 0 2px 4px rgba(56, 189, 248, 0.2);
`;

const ProductIcon = styled.div`
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #003554, #003554);
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
`;

const ProductName = styled.h3`
  color: #003554;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
`;

const ProductTagline = styled.p`
  color: #475569;
  font-size: 1rem;
  margin: 0;
`;

const ProductDescription = styled.p`
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-top: 1rem;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 1.5rem 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  padding: 0.5rem 0;
`;

const FeatureCheck = styled.span`
  color: #38bdf8;
  margin-right: 0.75rem;
  font-weight: bold;
`;

const ActionButton = styled.button`
  background: linear-gradient(to right, #003554, #003554);
  color: white;
  font-weight: 500;
  width: 100%;
  padding: 0.75rem 0;
  border-radius: 0.5rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(30, 58, 138, 0.2);
  }
`;

const IntegrationTextLight = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  text-align: center;
`;
const IntegrationTextBold = styled.span`
  color: white;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  text-align: center;
  font-weight: bold;
  text-decoration: underline;
  text-decoration-color: #38bdf8;
  text-underline-offset: 0.2rem;

  text-decoration-thickness: 0.1rem;
  text-decoration-style: solid;

`;



// Main Container
const Container = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: #ffffff;
  overflow-x: hidden;
  color: #333;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
`;


const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at 20% 35%, rgba(10, 37, 64, 0) 0%, rgba(10, 37, 64, 0.5) 100%);
  z-index: 1;
`;

const WaveShape = styled.div`
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 80px;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 3;
`;



// Integration Section
const IntegrationSection = styled.section`
  background: linear-gradient(135deg, #0a2540 0%, #1b4d89 100%);
  padding: 6rem 0;
  position: relative;
  
  @media (min-width: 768px) {
    padding: 10rem 0;
  }
`;

const IntegrationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const IntegrationCard = styled.div<IntegrationCardProps>`
  background: ${props => props.type === 'protheus' 
    ? 'linear-gradient(135deg, #0a2540, #1b4d89)'
    : 'linear-gradient(135deg, #4d9fff, #33d2ff)'};
  color: white;
  border-radius: 1.25rem;
  padding: 3rem;
  transition: all 0.4s ease;
  min-height: 22rem;
  box-shadow: 0 18px 40px rgba(10, 37, 64, 0.25);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 90% 10%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 60%);
    z-index: 1;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.type === 'protheus'
      ? '0 25px 50px rgba(10, 37, 64, 0.35)'
      : '0 25px 50px rgba(77, 159, 255, 0.35)'};
  }
`;

const IntegrationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const IntegrationIconContainer = styled.div`
  background: rgba(255, 255, 255, 0.15);
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.75rem;
  transition: transform 0.4s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  ${IntegrationCard}:hover & {
    transform: scale(1.15);
  }
`;

const IntegrationTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
`;

const IntegrationText = styled.p`
  font-size: 1.0625rem;
  opacity: 0.9;
  margin-bottom: 1rem;
  line-height: 1.7;
`;

// Stats Section
const StatsSection = styled.section`
  background: #f8fafd;
  color: #0a2540;
  padding: 6rem 0;
  position: relative;
  
  @media (min-width: 768px) {
    padding: 8rem 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid rgba(231, 239, 253, 0.8);
  border-radius: 1.25rem;
  padding: 2.5rem;
  text-align: center;
  box-shadow: 0 10px 40px rgba(10, 37, 64, 0.08);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(10, 37, 64, 0.15);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 0.75rem;
  background: linear-gradient(to right, #4d9fff, #33d2ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const StatText = styled.div`
  font-size: 1.125rem;
  color: #546581;
  font-weight: 500;
`;

// CTA Section
const CTASection = styled.section`
  background-color: white;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 10rem 0;
  }
`;

const CTABackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at 90% 90%, rgba(77, 159, 255, 0.08) 0%, rgba(51, 210, 255, 0) 70%);
  z-index: 0;
`;

const CTAContent = styled.div`
  max-width: 50rem;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #0a2540;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  color: #546581;
  margin-bottom: 3rem;
  line-height: 1.7;
`;

const CTAButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const CTAButton = styled.a`
  background: linear-gradient(to right, #4d9fff, #33d2ff);
  color: #0a2540;
  font-weight: 600;
  font-size: 1.25rem;
  padding: 1.125rem 2.25rem;
  border-radius: 0.75rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(77, 159, 255, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(77, 159, 255, 0.4);
  }
`;

const CTAText = styled.p`
  font-size: 1.125rem;
  color: #546581;
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export default EasyMovePage;

